import type { AutoResolvedSuggestion } from '@this+that/models';
import { create } from 'zustand';

import { AutoResolvedRowState } from '../../enums/autoResolvedRowState';
import { SuggestionsTab } from '../../enums/suggestionsTab';

interface AutoResolvedSuggestionsState {
  taskGroupId: string | null;
  items: AutoResolvedSuggestion[];
  rowStates: Record<string, AutoResolvedRowState>;
  restoredEntryIds: Set<string>;
  activeTab: SuggestionsTab;
  isLoading: boolean;
}

interface AutoResolvedSuggestionsActions {
  replaceItems: (taskGroupId: string, items: AutoResolvedSuggestion[]) => void;
  markRow: (entryId: string, rowState: AutoResolvedRowState) => void;
  markRestored: (entryId: string) => void;
  setActiveTab: (tab: SuggestionsTab) => void;
  setLoading: (isLoading: boolean) => void;
  reset: (taskGroupId: string | null) => void;
}

type AutoResolvedSuggestionsStore = AutoResolvedSuggestionsState &
  AutoResolvedSuggestionsActions;

const buildInitialState = (
  taskGroupId: string | null,
): AutoResolvedSuggestionsState => ({
  taskGroupId,
  items: [],
  rowStates: {},
  restoredEntryIds: new Set<string>(),
  activeTab: SuggestionsTab.PENDING,
  isLoading: false,
});

const isTransient = (rowState: AutoResolvedRowState | undefined): boolean =>
  rowState === AutoResolvedRowState.UNDONE ||
  rowState === AutoResolvedRowState.CONFIRMED;

const mergeServerItems = (
  previous: AutoResolvedSuggestion[],
  incoming: AutoResolvedSuggestion[],
  rowStates: Record<string, AutoResolvedRowState>,
): AutoResolvedSuggestion[] => {
  const incomingIds = new Set(incoming.map((item) => item.entry.id));
  const kept = previous.filter(
    (item) =>
      !incomingIds.has(item.entry.id) && isTransient(rowStates[item.entry.id]),
  );
  return [...incoming, ...kept];
};

export const useAutoResolvedSuggestionsStore =
  create<AutoResolvedSuggestionsStore>()((set) => ({
    ...buildInitialState(null),
    replaceItems: (taskGroupId, items) =>
      set((state) =>
        taskGroupId !== state.taskGroupId
          ? state
          : {
              items: mergeServerItems(state.items, items, state.rowStates),
            },
      ),
    markRow: (entryId, rowState) =>
      set((state) => ({
        rowStates: { ...state.rowStates, [entryId]: rowState },
      })),
    markRestored: (entryId) =>
      set((state) => ({
        restoredEntryIds: new Set(state.restoredEntryIds).add(entryId),
      })),
    setActiveTab: (tab) =>
      set((state) => {
        if (tab !== SuggestionsTab.PENDING) return { activeTab: tab };
        const rowStates = { ...state.rowStates };
        const items = state.items.filter((item) => {
          const id = item.entry.id;
          if (!isTransient(rowStates[id])) return true;
          delete rowStates[id];
          return false;
        });
        return { activeTab: tab, items, rowStates };
      }),
    setLoading: (isLoading) => set({ isLoading }),
    reset: (taskGroupId) => set(buildInitialState(taskGroupId)),
  }));
